import { useCallback } from "react";
import { useAlerting, useTranslation } from "src/hooks";

import {
  IExactGetOperation,
  IExactPayOperation,
  SwapRequest
} from "../../../../api/swap";
import { useChains } from "../../../../components/FetchConfig";
import { findDecimalByChainNameAndAsset } from "../../../../utils/helper";
import { useZusDialogStore } from "../../../../zustand/store";
import { getExchangeRateDisplay } from "../Content/helpers/getExchangeRateDisplay";
import { getMaxPayAmount } from "../Content/helpers/getMaxPayAmount";
import { getMinGetAmount } from "../Content/helpers/getMinGetAmount";
import { useCreateSwapRequestContext } from "../context";
import { TabTypes } from "../types";

interface UseQuoteProps {
  fields: any;
  setFields: (fields: any) => void;
  mode: TabTypes;
  payload: SwapRequest<IExactPayOperation | IExactGetOperation>;
  quoteApiFn: any;
  validateFunc: () => boolean;
}

export const useQuote = ({
  fields,
  setFields,
  mode,
  payload,
  quoteApiFn,
  validateFunc,
}: UseQuoteProps) => {
  const { selectChainByPieces } = useChains();
  const zusDialog = useZusDialogStore();
  const { alerting } = useAlerting();
  const { t } = useTranslation("createSwapRequest");

  const { filterFields } = useCreateSwapRequestContext();

  const { chain_type, chain_id } = selectChainByPieces({
    chain_name: filterFields.chainName,
  });

  const handleQuote = useCallback(async () => {
    let amountField, estimateField, minMaxField, apiEstimateAmountFieldName;

    if (mode === TabTypes.EXACT_PAY) {
      amountField = "exactPayAmount";
      estimateField = "estimateAmount";
      minMaxField = "getMin";
      apiEstimateAmountFieldName = "amount_get_est";
    } else {
      amountField = "exactGetAmount";
      estimateField = "estimateAmount";
      minMaxField = "payMax";
      apiEstimateAmountFieldName = "amount_pay_est";
    }

    const isCanProceed = validateFunc();

    if (!isCanProceed) {
      return;
    }

    zusDialog.openExtra("loadingDialog");
    const res = await quoteApiFn(payload);
    zusDialog.closeExtra();

    if (!res) {
      alerting("error", t("quoteFailed"));
      return;
    }

    const userInputAmount = fields[amountField];

    const estimatedAmount =
      res?.query_results?.[0]?.detail?.[apiEstimateAmountFieldName];

    if (userInputAmount !== "0" && estimatedAmount === 0) {
      alerting("error", t("insuffientSwapTokenError"));
      return;
    }

    const fromCurrencyDecimals = Number(
      findDecimalByChainNameAndAsset(
        String(chain_type),
        String(chain_id),
        filterFields.fromCurrency
      )
    );

    const toCurrencyDecimals = Number(
      findDecimalByChainNameAndAsset(
        String(chain_type),
        String(chain_id),
        filterFields.toCurrency
      )
    );

    const exchangeRate = getExchangeRateDisplay({
      fromCurrency: filterFields.fromCurrency,
      toCurrency: filterFields.toCurrency,
      exactAmount: fields[amountField],
      estimatedAmount: estimatedAmount,
      mode,
      fromCurrencyDecimals: fromCurrencyDecimals,
      toCurrencyDecimals: toCurrencyDecimals,
    });

    const minGet = getMinGetAmount({
      estimatedAmount: estimatedAmount,
      maxSlippage: filterFields.maxSlippage,
      toCurrencyDecimals: toCurrencyDecimals,
    });

    const maxPay = getMaxPayAmount({
      estimatedAmount: estimatedAmount,
      maxSlippage: filterFields.maxSlippage,
      fromCurrencyDecimals: fromCurrencyDecimals,
    });

    const valueMap: Record<string, string> = {
      getMin: minGet,
      payMax: maxPay,
    };

    // Create snapshot after successful quote
    const snapshot = {
      exactAmount: fields[amountField],
      maxSlippage: filterFields.maxSlippage,
    };

    setFields({
      ...fields,
      [estimateField]: estimatedAmount,
      exchangeRate,
      [minMaxField]: valueMap[minMaxField],
      amountSnapShot: snapshot,
      quotedTime: new Date().toISOString(),
    });
  }, [fields, setFields, filterFields, payload]);

  return { handleQuote };
};
